import type { TimelineEntry, TimelineEventKind } from '../api/types'
import { summariseTimeline } from '../domain/timeline'
import { Icon, type IconName } from './Icon'

/**
 * The vehicle's whole life on one axis, oldest first.
 *
 * Nothing here is new. It is the registration dates, the keuringen, the retrofitted objects and the
 * recalls that already have their own cards, merged into a single ordering — because the RDW
 * publishes those as four unrelated lists, and what a buyer wants to notice lives in the gaps
 * between them. A five-year run with no inspection, a tow bar fitted the month before the car
 * changed hands, a recall announced two owners ago: none of that is visible while the lists sit in
 * separate cards, and all of it is obvious here.
 *
 * Two things are deliberate and easy to undo by accident.
 *
 * **The order is the opposite of the inspection card's.** `inspection.history` and `recalls` lead
 * with the most recent entry because they are lists of the latest thing. This is a story and it
 * starts at the beginning; reversing it to "match" the card above would break the one thing the
 * section is for.
 *
 * **The future entry is drawn as future.** The APK expiry is the only date here that can lie ahead,
 * and it is marked with a hollow marker and a dashed rule so it does not read as something that has
 * already happened. Whether it *is* in the future is the API's answer, not a comparison against the
 * browser's clock: a report is cached for twelve hours, and a client deciding for itself would
 * eventually contradict the APK verdict elsewhere on the same page.
 *
 * `hasDefectProfile` is passed in rather than looked up because that card arrives from its own
 * endpoint after this one has rendered. It decides whether the future entry carries its forward
 * link, which is the only thing on this axis pointing at something that has not happened.
 */
export function VehicleTimeline({
  entries,
  hasDefectProfile,
}: {
  entries: TimelineEntry[]
  hasDefectProfile?: boolean
}) {
  const rows = summariseTimeline(entries, { hasDefectProfile })

  if (rows.length === 0) {
    return null
  }

  return (
    <ol className="lifeline">
      {rows.map((row, index) => (
        <li
          // Several events can share a date — an import registered the day it was inspected — so
          // the position is part of the identity. The list is never reordered or filtered.
          key={`${row.kind}-${row.date}-${index}`}
          className={`lifeline__item lifeline__item--${row.tone}`}
        >
          <span className="lifeline__marker" aria-hidden="true">
            <Icon name={ICONS[row.kind] ?? 'info'} size={14} />
          </span>

          <p className="lifeline__date">{row.date}</p>
          <h3 className="lifeline__title">
            {row.title}
            {/*
              The card is free and these entries are not, so the mark goes per entry rather than on
              the whole card — the same shape as the APK card, which is free with its history marked
              inside it. Icon only: a full "Premium" pill on every keuring of a car with a long
              history would be a column of yellow down the page, and the word is carried for screen
              readers instead.
            */}
            {row.premium && (
              <span className="lifeline__premium">
                <Icon name="unlock" size={11} />
                <span className="visually-hidden">Premium</span>
              </span>
            )}
            {/*
              On the title line, not under it: the defect profile lands seconds after the report, and
              a second line appearing then reflows every card below this one. The padlock sits inside
              the anchor with the text immediately after it, so it reads as marking where the link
              goes rather than as marking this entry, which is free.
            */}
            {row.lookahead && (
              <a className="lifeline__lookahead" href={row.lookahead.href}>
                <span className="lifeline__lookahead-mark">
                  <Icon name="unlock" size={11} />
                  {/* Part of the link's own name, so the mark reaches a screen reader as it does a
                      reader who can see the padlock: "Premium, wat gaat er bij dit model vaak mis?" */}
                  <span className="visually-hidden">Premium</span>
                </span>
                {row.lookahead.text}
              </a>
            )}
          </h3>
          {(row.detail || row.link) && (
            <p className="lifeline__detail">
              {row.detail}
              {row.detail && row.link ? ', ' : ''}
              {/*
                The number is the way back to the card this entry summarises. Nothing is lost if the
                anchor is broken, so it stays a plain link rather than anything that would look
                broken when it does not move.
              */}
              {row.link && (
                <a className="lifeline__link" href={row.link.href}>
                  {row.link.text}
                </a>
              )}
            </p>
          )}
        </li>
      ))}
    </ol>
  )
}

/**
 * One glyph per kind of event, from the set the report already inlines.
 *
 * They are for scanning rather than decoration: the value of the timeline is spotting a run of
 * keuringen, a gap, and then a recall, and doing that from the icons is quicker than reading every
 * heading.
 */
const ICONS: Record<TimelineEventKind, IconName> = {
  FirstAdmission: 'car',
  FirstAdmissionInNetherlands: 'globe',
  RegisteredToCurrentKeeper: 'check',
  Inspection: 'shield',
  EquipmentFitted: 'wrench',
  EquipmentRemoved: 'wrench',
  RecallAnnounced: 'warning',
  ApkExpiry: 'calendar',
}
